import React from 'react';
import { Alert, Spinner, RowActions, DataTable, type DataTableColumn } from '@/components/ui';
import { format } from 'date-fns';
import { ProjectDocument } from '../../../types/ProjectDocument';
import { useClientSort } from '../../../utils/useClientSort';

const SORT_VALUES: Record<string, (doc: ProjectDocument) => string | number | null | undefined> = {
  createdAt: (doc) => (doc.createdAt ? new Date(doc.createdAt).getTime() : null),
  attachmentName: (doc) => doc.attachmentName,
  attachmentNotes: (doc) => doc.attachmentNotes,
};

interface OtherDocumentListProps {
  documents: ProjectDocument[];
  loading?: boolean;
  onDownload: (doc: ProjectDocument) => void;
  onDelete: (docId: number) => Promise<void>;
  refreshingId?: number | null;
}

const OtherDocumentList: React.FC<OtherDocumentListProps> = ({
  documents,
  loading = false,
  onDownload,
  onDelete,
  refreshingId = null,
}) => {
  const { sorted, sortBy, sortDir, onSort } = useClientSort(documents, SORT_VALUES);
  const formatDate = (date: string | Date) => {
    try {
      const d = typeof date === 'string' ? new Date(date) : date;
      if (isNaN(d.getTime())) return 'Invalid date';
      return format(d, 'MMM dd, yyyy');
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className="tw-text-center tw-p-4">
        <Spinner>Loading...</Spinner>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <Alert variant="info" className="tw-text-center">
        No other documents found. Click “Add Document” to upload the first one.
      </Alert>
    );
  }

  const handleDownloadClick = (doc: ProjectDocument) => {
    if (doc.attachmentURL?.startsWith?.('placeholder://')) {
      alert('File upload failed. This document could not be uploaded to storage. Please try uploading the file again.');
      return;
    }
    onDownload(doc);
  };

  // This delete fired with no confirmation at all (ui-audit §0) — documents are unrecoverable.
  const handleDeleteClick = (doc: ProjectDocument) => {
    if (!window.confirm(`Delete "${doc.attachmentName}"? This cannot be undone.`)) return;
    onDelete(doc.id);
  };

  // File name is the identifying label, so it's the card title; Date is the secondary line.
  const columns: DataTableColumn<ProjectDocument>[] = [
    {
      key: 'attachmentName',
      header: 'File',
      sortable: true,
      mobile: 'title',
      cell: (doc) => (
        <span className="tw-truncate tw-inline-block" title={doc.attachmentName} style={{ maxWidth: 320 }}>
          {doc.attachmentName}
        </span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Date',
      sortable: true,
      mobile: 'subtitle',
      cell: (doc) => formatDate(doc.createdAt),
    },
    {
      key: 'attachmentNotes',
      header: 'Notes',
      sortable: true,
      cell: (doc) =>
        doc.attachmentNotes ? (
          <span className="tw-truncate tw-inline-block" title={doc.attachmentNotes} style={{ maxWidth: 250 }}>
            {doc.attachmentNotes}
          </span>
        ) : (
          <span className="tw-text-content-muted">-</span>
        ),
    },
    {
      key: 'actions',
      header: 'Actions',
      align: 'right',
      mobile: 'trailing',
      cell: (doc) => (
        <RowActions
          actions={[
            { kind: 'download', label: 'Download', onClick: () => handleDownloadClick(doc) },
            {
              kind: 'delete',
              label: 'Delete Document',
              onClick: () => handleDeleteClick(doc),
              disabled: refreshingId === doc.id,
            },
          ]}
        />
      ),
    },
  ];

  return (
    <div className="tw-w-full">
      <DataTable columns={columns} data={sorted} rowKey={(doc) => doc.id} striped hover sortBy={sortBy} sortDir={sortDir} onSort={onSort} />
    </div>
  );
};

export default OtherDocumentList;

